import React, { useCallback, useEffect, useState } from 'react';
import Joyride, { LIFECYCLE, STATUS } from 'react-joyride';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { uiManagerService } from 'v2/services/uiManager';

import type { CallBackProps, Props as IJoyProps, Step as IStepProps } from 'react-joyride';

interface IProps extends IJoyProps {
  name: string;
}

const defaultStepProps = {
  disableBeacon: true,
  disableCloseOnEsc: true,
  disableOverlayClose: true,
  showProgress: true,
};

const Tour = ({ name, steps, ...props }: IProps) => {
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [currentlyTour] = useObservable(() => uiManagerService.getCurrentlyTour(), []);
  const [isModalOpen] = useObservable(() => uiManagerService.getIsModalOpen(), []);
  const [tours] = useObservable(() => eduzzAccountsService.getTours(), []);
  const [saveTour] = useCallbackObservable(() => eduzzAccountsService.saveTourAction(name), []);
  const [loadingTours] = useObservable(() => eduzzAccountsService.getLoadingTours(), []);

  const [tourName] = useState(() => `tour-${name}`);

  const callback = useCallback(
    (data: CallBackProps) => {
      const userDidntSeeTour = !tours?.includes(name);

      const { status, lifecycle } = data;

      if (lifecycle === LIFECYCLE.COMPLETE && STATUS.FINISHED === status && userDidntSeeTour) {
        saveTour();
        uiManagerService.setCurrentlyTour(null);
      }
    },
    [name, saveTour, tours]
  );

  useEffect(() => {
    const userDidntSeeTour = tours !== null && !tours?.includes(name);
    const userIsNotSupport = user !== null && !user.is_support;
    const shouldShow = userDidntSeeTour && userIsNotSupport && !loadingTours;

    shouldShow && uiManagerService.setCurrentlyTour(tourName);
  }, [currentlyTour, loadingTours, name, tourName, tours, user]);

  const shouldShowTour = currentlyTour === tourName && !isModalOpen;
  const nSteps: IStepProps[] = steps.map((s: IStepProps) => ({ ...s, ...defaultStepProps }));

  return (
    <Joyride
      locale={{ back: 'Voltar', close: 'Fechar', last: 'Entendi', next: 'Próximo', skip: 'Pular' }}
      scrollToFirstStep={true}
      run={shouldShowTour}
      steps={nSteps}
      continuous={true}
      callback={callback}
      disableScrollParentFix
      styles={{
        overlay: {
          height: '100%',
        },
        options: {
          primaryColor: '#192542',
          textColor: '#000',
          zIndex: 1301,
        },
        buttonClose: {
          display: 'none',
        },
      }}
      {...props}
    />
  );
};

export default Tour;
